/**
 * Organization-Type-Based Owner Roles Configuration
 * 
 * Defines owner roles specific to each organization type.
 * Owner roles are functional responsibilities (e.g., "Loan Officer", "Kitchen Manager")
 * that differ based on the type of organization.
 */

export interface OrganizationTypeRoleConfig {
  ownerRoles: readonly string[];
  defaultRole?: string; // Optional default role for new users/templates
}

/**
 * Role configuration for each organization type
 */
export const ORGANIZATION_TYPE_ROLES: Record<string, OrganizationTypeRoleConfig> = {
  'Bank': {
    ownerRoles: [
      'Loan Officer',
      'Compliance Officer',
      'Credit Analyst',
      'Underwriter',
      'Loan Processor',
      'Branch Manager',
      'Risk Manager',
      'Operations Manager',
      'Owner/Admin'
    ] as const,
    defaultRole: 'Loan Officer'
  },
  
  'Restaurant': {
    ownerRoles: [
      'Kitchen Manager',
      'Site Manager',
      'Operations Manager',
      'Safety Officer',
      'Owner/Admin'
    ] as const,
    defaultRole: 'Kitchen Manager'
  },
  
  'Manufacturing': {
    ownerRoles: [
      'Site Manager',
      'Engineering Lead',
      'Maintenance Supervisor',
      'Safety Officer',
      'Operations Manager',
      'Owner/Admin'
    ] as const,
    defaultRole: 'Site Manager'
  },
  
  // Default/Generic roles (backward compatibility)
  // These are the original roles that work for most organization types
  'default': {
    ownerRoles: [
      'Site Manager',
      'Engineering Lead',
      'Kitchen Manager',
      'Maintenance Supervisor',
      'Owner/Admin',
      'HR Manager',
      'Facilities Manager',
      'Operations Manager',
      'Safety Officer'
    ] as const,
    defaultRole: 'Site Manager'
  }
};

/**
 * Get owner roles for a specific organization type
 * Falls back to 'default' if type not found or not provided
 * 
 * @param organizationType - The organization type (e.g., 'Bank', 'Restaurant')
 * @returns Array of owner roles for that organization type
 */
export function getOwnerRolesForOrganizationType(
  organizationType: string | null | undefined
): readonly string[] {
  if (!organizationType) {
    return ORGANIZATION_TYPE_ROLES['default'].ownerRoles;
  }
  
  // Try exact match first (case-sensitive)
  let config = ORGANIZATION_TYPE_ROLES[organizationType];
  
  if (!config) {
    // Try case-insensitive match by checking all keys
    const orgTypeLower = organizationType.toLowerCase();
    const matchingKey = Object.keys(ORGANIZATION_TYPE_ROLES).find(
      key => key.toLowerCase() === orgTypeLower
    );
    if (matchingKey) {
      config = ORGANIZATION_TYPE_ROLES[matchingKey];
    }
  }
  
  return config?.ownerRoles || ORGANIZATION_TYPE_ROLES['default'].ownerRoles;
}

/**
 * Get default owner role for a specific organization type
 * Falls back to default organization's default role
 * 
 * @param organizationType - The organization type
 * @returns Default role string or undefined
 */
export function getDefaultOwnerRoleForOrganizationType(
  organizationType: string | null | undefined
): string | undefined {
  if (!organizationType) {
    return ORGANIZATION_TYPE_ROLES['default'].defaultRole;
  }
  
  const config = ORGANIZATION_TYPE_ROLES[organizationType];
  return config?.defaultRole || ORGANIZATION_TYPE_ROLES['default'].defaultRole;
}

/**
 * Check if a role is valid for a specific organization type
 * 
 * @param role - The role to check
 * @param organizationType - The organization type
 * @returns True if role is valid for the organization type
 */
export function isRoleValidForOrganizationType(
  role: string,
  organizationType: string | null | undefined
): boolean {
  const validRoles = getOwnerRolesForOrganizationType(organizationType);
  return validRoles.includes(role);
}

/**
 * Get all possible owner roles across all organization types
 * Used for migration, admin views, etc.
 * 
 * @returns Sorted array of all unique owner roles
 */
export function getAllOwnerRoles(): string[] {
  const allRoles = new Set<string>();
  Object.values(ORGANIZATION_TYPE_ROLES).forEach(config => {
    config.ownerRoles.forEach(role => allRoles.add(role));
  });
  return Array.from(allRoles).sort();
}

/**
 * Get organization types that support a specific role
 * Useful for migration and validation
 * 
 * @param role - The role to check
 * @returns Array of organization types that support this role
 */
export function getOrganizationTypesForRole(role: string): string[] {
  const orgTypes: string[] = [];
  Object.entries(ORGANIZATION_TYPE_ROLES).forEach(([orgType, config]) => {
    if (config.ownerRoles.includes(role)) {
      orgTypes.push(orgType);
    }
  });
  return orgTypes;
}

